import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building2, ListChecks, UserRound, Users } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { Tabs } from '../../components/ui/Tabs';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import fieldWorkService from '../../services/fieldWork';
import type { LabourProvider } from '../../types/fieldWork';
import WorkersPanel from '../../components/fieldWork/WorkersPanel';
import CrewsPanel from '../../components/fieldWork/CrewsPanel';
import ProvidersPanel from '../../components/fieldWork/ProvidersPanel';
import ActivityTypesPanel from '../../components/fieldWork/ActivityTypesPanel';

type TabKey = 'workers' | 'crews' | 'providers' | 'types';

const TABS: { key: TabKey; label: string; icon: ReactNode }[] = [
  { key: 'workers', label: 'Workers', icon: <UserRound className="h-4 w-4" /> },
  { key: 'crews', label: 'Crews', icon: <Users className="h-4 w-4" /> },
  { key: 'providers', label: 'Labour providers', icon: <Building2 className="h-4 w-4" /> },
  { key: 'types', label: 'Work types', icon: <ListChecks className="h-4 w-4" /> },
];

/**
 * /jobs/workforce — the people side of Farm Jobs: the roster (with or without
 * app accounts), crews, labour providers and the org's types of work. Gated in
 * App.tsx on a farm_jobs supervisor / creator / admin seat.
 */
export default function Workforce() {
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab') as TabKey | null;
  const tab: TabKey = TABS.some(t => t.key === tabParam) ? (tabParam as TabKey) : 'workers';

  const { hasAccess: canEditProviders } = useModuleLicense('farm_jobs', ['creator', 'admin']);
  const { hasAccess: isAdmin } = useModuleLicense('farm_jobs', 'admin');

  const [providers, setProviders] = useState<LabourProvider[]>([]);
  const loadProviders = useCallback(() => {
    fieldWorkService.getProviders(true).then(setProviders).catch(() => setProviders([]));
  }, []);
  useEffect(() => { loadProviders(); }, [loadProviders]);

  return (
    <>
      <PageHeader
        title="Workforce"
        subtitle="Workers, crews and labour providers for farm jobs"
        icon={<Users className="h-6 w-6 text-white" />}
      />
      <div className="max-w-5xl mx-auto px-4 py-6">
        <Tabs
          tabs={TABS}
          active={tab}
          onChange={k => setParams(k === 'workers' ? {} : { tab: k }, { replace: true })}
          className="mb-4"
        />
        <div className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6">
          {tab === 'workers' && <WorkersPanel providers={providers} />}
          {tab === 'crews' && <CrewsPanel providers={providers} />}
          {tab === 'providers' && (
            <ProvidersPanel providers={providers} canEdit={canEditProviders} onChanged={loadProviders} />
          )}
          {tab === 'types' && <ActivityTypesPanel canEdit={isAdmin} />}
        </div>
      </div>
    </>
  );
}
